import { useCallback, useMemo } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useSelector, useDispatch } from 'react-redux';
import { forceBulkLoadProductData } from '../../store/products/products.actions';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';
import { convertAndSumWeightValues } from '../../services/unit-services/weights/weight-convert-and-sum-service';
import { convertDimensions } from '../../services/unit-services/dimensions/dimension-convert-service';

import { saveShippingDefaults } from '../../store/products/products.api';

const configureWeight = (packages, sellerDefaultWeightUnit) => {
  return convertAndSumWeightValues(
    (packages || []).map((p) => p.weight || {}),
    sellerDefaultWeightUnit,
  );
};

const configureDimensions = (packages, sellerDefaultDimensionUnit) => {
  return convertDimensions(packages?.[0]?.dimensions || {}, sellerDefaultDimensionUnit);
};

export const useSaveProductShippingDefaults = (fulfillmentPlanId) => {
  const dispatch = useDispatch();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const productId = (fulfillmentPlan?.items?.length && fulfillmentPlan.items[0].productId) || '';
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const { packages, serviceCollectionGuid } = useFulfillmentPlannerContext();
  const weight = configureWeight(packages, sellerDefaultWeightUnit) || 0;
  const productFulfillmentSku =
    (fulfillmentPlan && fulfillmentPlan.items && fulfillmentPlan.items.length && fulfillmentPlan.items[0].sku) || '';
  const sizeUnit = useSelector((state) => Global.selectSellerDefaultDimensionUnit(state));
  const dimensions = configureDimensions(packages, sizeUnit) || {
    length: 0,
    width: 0,
    height: 0,
  };
  const quantity = (fulfillmentPlan?.items?.length && fulfillmentPlan.items[0].quantity) || 0;
  const adjustedWeight = quantity === 1 ? weight.value : weight.value / quantity;
  const { length, width, height } = dimensions;
  const packageTypeId = (fulfillmentPlan?.packages?.length && fulfillmentPlan.packages[0].packageTypeId) || '';
  const confirmationId = fulfillmentPlan?.options?.confirmationEnumId || '';
  const isInternational = fulfillmentPlan?.international?.isInternational || false;
  const sellerLabelProviderId = fulfillmentPlan?.sellerLabelProviderId || '';
  const serviceId = fulfillmentPlan?.serviceId;

  const shippingDefaults = useMemo(
    () => ({
      sellerLabelProviderId,
      productId,
      productFulfillmentSku,
      sizeUnit,
      serviceId,
      packageTypeId,
      confirmationId,
      isInternational,
      weight: adjustedWeight,
      weightUnitOfMeasureId: weight.unit,
      length,
      width,
      height,
      dimensionUnitOfMeasureId: dimensions.unit,
      serviceCollectionGuid,
      fulfillmentPlanId,
    }),
    [
      adjustedWeight,
      confirmationId,
      dimensions.unit,
      fulfillmentPlanId,
      height,
      isInternational,
      length,
      packageTypeId,
      productFulfillmentSku,
      productId,
      sellerLabelProviderId,
      serviceId,
      serviceCollectionGuid,
      sizeUnit,
      weight.unit,
      width,
    ],
  );

  const [errors, setErrors] = useAsyncSafeState();

  const asyncSaveShippingDefaults = useCallback(() => {
    // clear the error and data state on each call since we could make multiple calls for component mounted a single time
    setErrors(null);

    const save = async () => {
      try {
        await saveShippingDefaults(shippingDefaults);
        dispatch(forceBulkLoadProductData([productId]));
      } catch (e) {
        setErrors(e);
      }
    };
    return save();
  }, [dispatch, productId, setErrors, shippingDefaults]);

  return useMemo(
    () => ({
      asyncSaveShippingDefaults,
      errors,
    }),
    [asyncSaveShippingDefaults, errors],
  );
};
